import {
  useCreateDeviceMutation,
  useDeleteDeviceMutation,
  useUpdateDeviceMutation,
} from "@hooks";
import {
  ComponentConfigurationType,
  ComponentType,
  Device,
  Organization,
  Plan,
  SimpleSubscription,
  SubComponentConfigurationType,
  SubComponentType,
} from "@models";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Checkbox,
  Collapse,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import {
  CreateDeviceUiState,
  CreationModalComponentState,
  CreationModalSubComponentState,
} from "@types";
import { captureSentryException, scrollToElement } from "@utils";
import { ComponentState, useState } from "react";
import { useParams } from "react-router-dom";
import { FreePlanLimitModalItemContent } from "src/components/DeviceCreation/modal-list-items/MyDeviceModalComponents/FreePlanLimitModalItemContent";
import { GeneratedByAIBadge } from "src/components/DeviceCreation/modal-list-items/MyDeviceModalComponents/GeneratedByAIBadge";
import { HardwareComponentConfiguration } from "src/components/DeviceCreation/modal-list-items/MyDeviceModalComponents/HardwareComponentConfiguration";
import { MyDeviceModalItemSection } from "src/components/DeviceCreation/modal-list-items/MyDeviceModalComponents/MyDeviceModalItemSection";
import { SoftwareComponentConfiguration } from "src/components/DeviceCreation/modal-list-items/MyDeviceModalComponents/SoftwareComponentConfiguration";
import {
  getCleanDevice,
  initialElectricalSubComponentState,
  initialMechanicalSubComponentState,
  initialSoftwareSubComponentState,
  isFormInvalid,
  newHardwareComponentInitialState,
  newSoftwareComponentInitialState,
} from "src/components/DeviceCreation/modal-list-items/MyDeviceModalComponents/utils";
import ConfirmationModal from "src/components/Form/ConfirmationModal";
import { useSnackbar } from "src/components/Snackbar/useSnackbar";
import { YesNo } from "src/components/YesNo/YesNo";
import { shouldPayForNextNewDevice } from "src/utils/entitlements"; // TODO: Why does @utils import breaks tests?

export const MyDeviceModalContent = ({
  subscription,
  devices,
  org,
  setNewDevice,
  newDevice,
  deviceIsAiGenerated,
  setEditDevice,
}: {
  subscription: SimpleSubscription;
  devices: Device[];
  org: Organization;
  setNewDevice: (newDevice: CreateDeviceUiState) => void;
  newDevice: CreateDeviceUiState;
  deviceIsAiGenerated: boolean;
  device?: Device;
  setEditDevice?: (editDevice: boolean) => void;
}) => {
  const { orgId = "", deviceId = "" } = useParams();
  const deleteDeviceMutation = useDeleteDeviceMutation();
  const createDeviceMutation = useCreateDeviceMutation();
  const updateDevice = useUpdateDeviceMutation({
    orgId,
    deviceId,
  });
  const { showSnackbar } = useSnackbar();

  const [billingConfirmed, setBillingConfirmed] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [devicesNamesToDelete, setDevicesNamesToDelete] = useState<string[]>(
    []
  );
  const [deviceCreationLoading, setDeviceCreationLoading] = useState(false);

  const updateProductComponent = (component: CreationModalComponentState) => {
    setNewDevice({
      ...newDevice,
      components: newDevice.components.map((c) =>
        c.index === component.index ? component : c
      ),
    });
  };

  const updateSubComponent = (
    component: CreationModalComponentState,
    subComponent: CreationModalSubComponentState
  ) => {
    setNewDevice({
      ...newDevice,
      components: newDevice.components.map((c) =>
        c.index === component.index
          ? {
              ...c,
              subComponents: c.subComponents?.map((sc) =>
                sc.index === subComponent.index ? subComponent : sc
              ),
            }
          : c
      ),
    });
  };

  const toggleProductComponentCollapse = (component: ComponentState) => {
    setNewDevice({
      ...newDevice,
      components: newDevice.components.map((c) =>
        c.index === component.index ? { ...c, collapsed: !c.collapsed } : c
      ),
    });

    scrollToElement(`component-${component.index}`);
  };

  const getComponentConfigurationValue = (
    component: CreationModalComponentState,
    type: ComponentConfigurationType
  ) => {
    const value = component.configuration.find(
      (item) => item.type === type
    )?.value;
    switch (value) {
      case "true":
        return true;
      case "false":
        return false;
      default:
        return value;
    }
  };

  const getSubComponentConfigurationValue = (
    subComponent: CreationModalSubComponentState,
    type: SubComponentConfigurationType
  ) => {
    return subComponent.configuration?.find((item) => item.type === type)
      ?.value;
  };

  const hasHardware = newDevice.components.some(
    (c) => c.type === ComponentType.HARDWARE
  );

  const hasSoftware = newDevice.components.some(
    (c) => c.type === ComponentType.SOFTWARE
  );

  const addSubComponent = (
    type: SubComponentType,
    component: CreationModalComponentState,
    name: string
  ) => {
    const initialState =
      type === SubComponentType.SOFTWARE
        ? initialSoftwareSubComponentState
        : type === SubComponentType.ELECTRICAL
          ? initialElectricalSubComponentState
          : {
              type,
              name,
              description: "",
              configuration: [],
            };
    const newSubComponent = {
      index: (component.subComponents?.length || 0) + 1,
      ...initialState,
    };
    setNewDevice({
      ...newDevice,
      components: newDevice.components.map((c) =>
        c.index === component.index
          ? {
              ...c,
              subComponents: [...(c.subComponents || []), newSubComponent],
            }
          : c
      ),
    });
  };

  const removeAllSubComponentsByType = (
    component: CreationModalComponentState,
    type: SubComponentType
  ) => {
    setNewDevice({
      ...newDevice,
      components: newDevice.components.map((c) =>
        c.index === component.index
          ? {
              ...c,
              subComponents: c.subComponents?.filter((sc) => sc.type !== type),
            }
          : c
      ),
    });
  };

  const removeAllComponentsByType = (type: ComponentType) => {
    setNewDevice({
      ...newDevice,
      components: newDevice.components.filter((c) => c.type !== type),
    });
  };

  const updateComponentConfigurationValue = (
    component: CreationModalComponentState,
    type: ComponentConfigurationType,
    value: string
  ) => {
    updateProductComponent({
      ...component,
      // If the configuration already contains the item, update it, otherwise add it
      configuration: component.configuration.some((item) => item.type === type)
        ? component.configuration.map((item) =>
            item.type === type ? { ...item, value } : item
          )
        : [...component.configuration, { type, value }],
    });
  };

  const getHighestComponentIndex = (
    components: CreationModalComponentState[]
  ) => {
    return components.reduce(
      (max, component) => Math.max(max, component.index),
      0
    );
  };

  const addProductComponent = (type: ComponentType) => {
    const initialState =
      type === ComponentType.HARDWARE
        ? newHardwareComponentInitialState
        : newSoftwareComponentInitialState;

    const oldComponents = newDevice.components.map((c) => ({
      ...c,
      collapsed: true,
    }));
    const newComponent: CreationModalComponentState = {
      index: getHighestComponentIndex(newDevice.components) + 1,
      ...initialState,
      // Hardware components always have mechanical parts
      ...(type === ComponentType.HARDWARE && {
        subComponents: [
          {
            index: 1,
            ...initialMechanicalSubComponentState,
          },
        ],
      }),
    };
    setNewDevice({
      ...newDevice,
      components: [...oldComponents, newComponent],
    });

    scrollToElement(`component-${newComponent.index}`);
  };

  const removeProductComponent = (component: ComponentState) => {
    setNewDevice({
      ...newDevice,
      components: newDevice.components.filter(
        (c) => c.index !== component.index
      ),
    });
  };

  const updateSubComponentConfigurationValue = (
    component: CreationModalComponentState,
    subComponent: CreationModalSubComponentState,
    type: SubComponentConfigurationType,
    value: string
  ) => {
    updateSubComponent(component, {
      ...subComponent,
      configuration: subComponent.configuration?.some(
        (item) => item.type === type
      )
        ? subComponent.configuration?.map((item) =>
            item.type === type ? { ...item, value } : item
          )
        : [...(subComponent.configuration || []), { type, value }],
    });
  };

  const nextDeviceIsPaidCreation = shouldPayForNextNewDevice(org, devices);

  const isFreePlan = subscription.products.includes(Plan.FREE);

  if (!setEditDevice && isFreePlan && nextDeviceIsPaidCreation) {
    return <FreePlanLimitModalItemContent />;
  }

  const isEU = newDevice.isMDR || newDevice.isIVDR;

  const getDemoDevices = () => {
    return devices.filter((device) => device.isDemoDevice);
  };

  const handleCreateDevice = async () => {
    try {
      setDeviceCreationLoading(true);
      const demoDevices = getDemoDevices();

      if (demoDevices.length > 0) {
        if (!confirmModalOpen) {
          setDevicesNamesToDelete(
            getDemoDevices().map((device) => device.name)
          );
          setConfirmModalOpen(true);
        } else {
          demoDevices.forEach((device) => {
            deleteDeviceMutation.mutateAsync({
              orgId: org.id,
              id: device.id,
            });
          });
          await createDeviceMutation.mutateAsync({
            device: getCleanDevice(newDevice),
            orgId: org.id,
          });
        }
      } else {
        await createDeviceMutation.mutateAsync({
          device: getCleanDevice(newDevice),
          orgId: org.id,
        });
      }
      setDeviceCreationLoading(false);
    } catch (error) {
      console.error("Error creating device:", error);
      captureSentryException(error);
      showSnackbar((error as Error).message || "Failed to create new device.");
    }
  };

  const handleUpdateDevice = () => {
    updateDevice.mutate({
      data: newDevice,
    });
    setEditDevice && setEditDevice(false);
  };

  return (
    <div className="flex flex-col gap-y-4">
      {!setEditDevice && (
        <div className="flex flex-col gap-y-2 mb-2">
          <Typography variant="body1">
            Fill in your device's details and create it below.
          </Typography>
          {deviceIsAiGenerated && (
            <GeneratedByAIBadge
              scrollToElement={() => scrollToElement("create")}
            />
          )}
        </div>
      )}
      <MyDeviceModalItemSection>
        <TextField
          label="Device Name"
          required
          fullWidth
          value={newDevice?.name}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setNewDevice({ ...newDevice, name: event.target.value });
          }}
        />
        <div className="flex flex-col gap-y-2">
          <TextField
            label="Description"
            required
            fullWidth
            multiline
            minRows={3}
            value={newDevice?.description}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setNewDevice({
                ...newDevice,
                description: event.target.value,
              });
            }}
          />
          <Alert severity="info">
            Provide a detailed description of your device. Every detail, like
            special features, materials, or the users and patients can be
            essential in order to give you the best possible suggestions in the
            future.
          </Alert>
        </div>
        <YesNo
          question="Do you want to sell your device in the US?"
          value={!!newDevice.isFDA}
          onChange={(value) => {
            value === "true"
              ? setNewDevice({ ...newDevice, isFDA: true })
              : setNewDevice({ ...newDevice, isFDA: false });
          }}
        />
        <YesNo
          question="Do you want to sell your device in the EU?"
          value={isEU}
          onChange={(value) => {
            setNewDevice({
              ...newDevice,
              isMDR: value === "true",
              ...(value === "false" && { isIVDR: false }),
            });
          }}
        />
        <Collapse in={isEU}>
          <YesNo
            question="Does your device analyze human samples (e.g., blood, urine, tissue) to provide medical or diagnostic information (aka is your device an in vitro diagnostic device / IVD)?"
            value={!!newDevice.isIVDR}
            onChange={(value) => {
              setNewDevice({
                ...newDevice,
                isIVDR: value === "true",
                isMDR: value === "false",
              });
            }}
          />
        </Collapse>
        <YesNo
          question="Do you want to sell your device in the UK?"
          value={!!newDevice.isUK}
          onChange={(value) => {
            value === "true"
              ? setNewDevice({ ...newDevice, isUK: true })
              : setNewDevice({ ...newDevice, isUK: false });
          }}
        />
        <YesNo
          question="Do you want to sell your device in Canada?"
          value={!!newDevice.isCanada}
          onChange={(value) => {
            value === "true"
              ? setNewDevice({ ...newDevice, isCanada: true })
              : setNewDevice({ ...newDevice, isCanada: false });
          }}
        />
      </MyDeviceModalItemSection>
      <MyDeviceModalItemSection>
        <div>
          <Typography variant="h5">Physical Components</Typography>
          <Alert severity="info" className="mb-2">
            A physical component is a standalone physical part of the device
            that might contain electrical, mechanical, firmware or software
            parts. Examples are a measuring device, a smartwatch or a pacemaker.
          </Alert>
          <div>
            <YesNo
              question="Does the product have physical components?"
              value={!!hasHardware}
              onChange={(value) =>
                value === "true"
                  ? addProductComponent(ComponentType.HARDWARE)
                  : removeAllComponentsByType(ComponentType.HARDWARE)
              }
            />
          </div>
          {!!hasHardware && (
            <HardwareComponentConfiguration
              newDevice={newDevice}
              toggleProductComponentCollapse={toggleProductComponentCollapse}
              updateProductComponent={updateProductComponent}
              updateComponentConfigurationValue={
                updateComponentConfigurationValue
              }
              getComponentConfigurationValue={getComponentConfigurationValue}
              getSubComponentConfigurationValue={
                getSubComponentConfigurationValue
              }
              addSubComponent={addSubComponent}
              removeAllSubComponentsByType={removeAllSubComponentsByType}
              updateSubComponent={updateSubComponent}
              updateSubComponentConfigurationValue={
                updateSubComponentConfigurationValue
              }
              removeProductComponent={removeProductComponent}
              addProductComponent={addProductComponent}
            />
          )}
        </div>
      </MyDeviceModalItemSection>
      <MyDeviceModalItemSection>
        <div>
          <Typography variant="h5">Standalone Software</Typography>
          <Alert severity="info">
            A standalone software component might be a mobile app, a web app, or
            a software package that is not part of any hardware. If you have
            components that do separate things like a patient facing app and a
            practitioner web app you want to specify them as separate components
            so that you can handle releases independently.
          </Alert>
        </div>
        <YesNo
          question="Does the product have standalone software?"
          value={!!hasSoftware}
          onChange={(value) =>
            value === "true"
              ? addProductComponent(ComponentType.SOFTWARE)
              : removeAllComponentsByType(ComponentType.SOFTWARE)
          }
        />
        {!!hasSoftware && (
          <SoftwareComponentConfiguration
            newDevice={newDevice}
            toggleProductComponentCollapse={toggleProductComponentCollapse}
            updateProductComponent={updateProductComponent}
            updateComponentConfigurationValue={
              updateComponentConfigurationValue
            }
            getComponentConfigurationValue={getComponentConfigurationValue}
            removeProductComponent={removeProductComponent}
            addProductComponent={addProductComponent}
          />
        )}
      </MyDeviceModalItemSection>
      <MyDeviceModalItemSection>
        <div className="flex gap-x-4 justify-end">
          {!setEditDevice && nextDeviceIsPaidCreation && (
            <FormControlLabel
              control={
                <Checkbox
                  className="ml-1"
                  checked={billingConfirmed}
                  onChange={(e) => {
                    setBillingConfirmed(e.target.checked);
                  }}
                />
              }
              label="I confirm that I will be billed for this additional device"
              labelPlacement="start"
            />
          )}
          <LoadingButton
            disabled={
              isFormInvalid(newDevice) ||
              (!setEditDevice && nextDeviceIsPaidCreation && !billingConfirmed)
            }
            variant="contained"
            color="success"
            id="create"
            loading={deviceCreationLoading}
            onClick={() => {
              if (setEditDevice) {
                handleUpdateDevice();
              } else {
                handleCreateDevice();
              }
            }}
          >
            {setEditDevice ? "Update" : "Create"}
          </LoadingButton>
        </div>
      </MyDeviceModalItemSection>

      {/* Confirmation Modal for demo device deletion, if they exist */}
      {!setEditDevice && (
        <ConfirmationModal
          open={confirmModalOpen}
          onClose={() => setConfirmModalOpen(false)}
          onConfirm={handleCreateDevice}
          title="Demo devices will be deleted"
          content={`We will delete demo device(s): "${devicesNamesToDelete.join(
            ", "
          )}" to create your requested device. Proceed?`} // Need to keep text as state or else the text is deleted when devices deleted
          loading={deviceCreationLoading}
        />
      )}
    </div>
  );
};
